import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BILLING_QUERY_KEY } from "./useBilling";
import apiClient from "../client";

export const ADD_ONS_QUERY_KEY = ["add-ons"] as const;

/** Scr.Enums.AddOnType.TradingViewScripts: the add-on that invites a TradingView account. */
export const TRADINGVIEW_SCRIPTS_ADD_ON_TYPE = 1;

/** The customer's own add-on, while it gives them access. */
export interface HeldAddOnDto {
  id: string;
  activeFrom: string;
  /** Null while it runs on. Set when it has a fixed term, or it is ending. */
  activeTo: string | null;
  /** Billed here, so the customer can cancel it themselves. */
  boughtHere: boolean;
  /** When it was cancelled, which ends it at activeTo. */
  cancelledAt: string | null;
  /** Staff cancelled it, so it's theirs to resume, not the customer's. */
  cancelledByStaff: boolean;
  /** Null when nothing here bills it. */
  billingCycle: "Monthly" | "Yearly" | null;
  /** What it costs each cycle, as when it was bought. Null when nothing here bills it. */
  priceUsd: number | null;
  /** When it is next billed. Null when it won't be. */
  renewsOn: string | null;
}

export interface AddOnDto {
  id: string;
  /** Scr.Enums.AddOnType. */
  type: number;
  name: string;
  description: string;
  helpArticleSlug: string | null;
  /** Null when it isn't sold monthly. */
  monthlyPriceUsd: number | null;
  /** Null when it isn't sold yearly. */
  yearlyPriceUsd: number | null;
  /** Null when the customer doesn't hold it. */
  held: HeldAddOnDto | null;
}

interface ListAddOnsResponse {
  items: AddOnDto[];
}

/** Every add-on on sale, and any other the customer holds. */
export function useAddOns() {
  return useQuery<AddOnDto[]>({
    queryKey: ADD_ONS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListAddOnsResponse>("/add-ons");
      return res.data.items;
    },
  });
}

export interface CancelAddOnResponse {
  customerAddOnExternalId: string;
  activeTo: string;
}

/** Ends an add-on the customer bought; access runs to the end of the paid period. */
export function useCancelAddOn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (addOnId: string) => {
      const res = await apiClient.post<CancelAddOnResponse>(
        `/customers/me/add-ons/${encodeURIComponent(addOnId)}/cancel`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADD_ONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

export function useResumeAddOn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (addOnId: string) => {
      const res = await apiClient.post(
        `/customers/me/add-ons/${encodeURIComponent(addOnId)}/resume`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADD_ONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/** Ending, but the customer's own cancel, so they can take it back until it ends. */
export function canResumeAddOn(
  held: Pick<HeldAddOnDto, "cancelledAt" | "cancelledByStaff" | "activeTo">,
): boolean {
  return (
    held.cancelledAt !== null &&
    !held.cancelledByStaff &&
    (held.activeTo === null || new Date(held.activeTo).getTime() > Date.now())
  );
}
